import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getAllLogs, getAttendanceUsers, getShifts } from './attendanceApi';
import { format, startOfWeek, endOfWeek, isWithinInterval, parseISO, differenceInMinutes, addDays, getDay, isSameDay, isAfter } from 'date-fns';
import { BarChart3, Calendar, ArrowLeft, Download, Filter, Banknote, ChevronDown, ChevronUp, Clock, Zap } from 'lucide-react';
import * as XLSX from 'xlsx';
import { es } from 'date-fns/locale';

export function WeeklyReport({ onBack }) {
  const [users, setUsers] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedUserId, setExpandedUserId] = useState(null);

  const EXTRA_RATE_NORMAL = 5000;
  const EXTRA_RATE_SUNDAY = 7500;
  const TOLERANCE_MINUTES = 15;

  const [startDate, setStartDate] = useState(format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Pasamos la fecha de inicio seleccionada al backend para que traiga datos históricos de cualquier mes
      const [u, s, l] = await Promise.all([
        getAttendanceUsers(), 
        getShifts(startDate),
        getAllLogs(startDate)
      ]);
      setUsers(u);
      setShifts(s);
      setLogs(l);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [startDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDuration = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  const reportData = useMemo(() => {
    const start = parseISO(startDate);
    const end = parseISO(endDate);

    return users.map(user => {
      let totalScheduledMinutes = 0;
      let totalBasePayment = 0;
      let totalWorkedMinutesNet = 0;
      let totalBreakMinutes = 0;
      let totalAuthorizedExtraPayment = 0;
      let totalUnauthorizedExtraPayment = 0;
      const dailyDetails = [];

      // 1. Turnos y Logs del usuario
      const userShifts = shifts.filter(s => {
        const sDate = parseISO(s.start_time);
        return s.user_id === user.id && isWithinInterval(sDate, { start, end: addDays(end, 1) });
      });

      const userLogs = logs
        .filter(l => l.user_id === user.id)
        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

      // 2. Emparejar Entradas y Salidas (Soportando cruce de medianoche)
      const pairs = [];
      const usedLogIds = new Set();

      userLogs.forEach(log => {
        if (log.type === 'in' && !usedLogIds.has(log.id)) {
          const checkOut = userLogs.find(l => 
            l.type === 'out' && 
            !usedLogIds.has(l.id) && 
            isAfter(new Date(l.timestamp), new Date(log.timestamp)) &&
            differenceInMinutes(new Date(l.timestamp), new Date(log.timestamp)) < 900
          );
          
          pairs.push({
            in: log,
            out: checkOut || null,
            duration: checkOut ? differenceInMinutes(new Date(checkOut.timestamp), new Date(log.timestamp)) : 0,
            isAuthorized: checkOut?.is_overtime_authorized || false
          });

          usedLogIds.add(log.id);
          if (checkOut) usedLogIds.add(checkOut.id);
        }
      });

      // 3. Procesar día a día
      let current = start;
      while (current <= end) {
        const dateKey = format(current, 'yyyy-MM-dd');
        const dayShifts = userShifts.filter(s => format(parseISO(s.start_time), 'yyyy-MM-dd') === dateKey);
        const dayPairs = pairs.filter(p => format(new Date(p.in.timestamp), 'yyyy-MM-dd') === dateKey);

        let dayWorkedGross = dayPairs.reduce((acc, p) => acc + p.duration, 0);
        let daySchedMin = 0;
        let dayBreakMin = 0;
        let dayBasePay = 0;

        dayShifts.forEach(s => {
          let dur = differenceInMinutes(parseISO(s.end_time), parseISO(s.start_time));
          if (s.breaks) {
            s.breaks.forEach(b => {
              const bDur = differenceInMinutes(parseISO(b.end_time), parseISO(b.start_time));
              dur -= bDur;
              dayBreakMin += bDur;
            });
          }
          daySchedMin += dur;
          dayBasePay += (s.amount || 0);
        });

        let dayWorkedNet = Math.max(0, dayWorkedGross - dayBreakMin);
        let dayExtraAuthPay = 0;
        let dayExtraUnauthPay = 0;
        let isAuthorized = dayPairs.some(p => p.isAuthorized);

        if (dayWorkedNet > 0) {
          let dayExtraMin = 0;
          if (daySchedMin > 0) {
            dayExtraMin = Math.max(0, dayWorkedNet - daySchedMin);
            if (dayExtraMin <= TOLERANCE_MINUTES) dayExtraMin = 0;
          } else {
            dayExtraMin = dayWorkedNet;
          }

          if (dayExtraMin > 0) {
            const isSunday = getDay(current) === 0;
            const rate = isSunday ? EXTRA_RATE_SUNDAY : EXTRA_RATE_NORMAL;
            const amount = Math.round((dayExtraMin / 60) * rate);
            if (isAuthorized) dayExtraAuthPay = amount;
            else dayExtraUnauthPay = amount;
          }
        }

        if (daySchedMin > 0 || dayWorkedGross > 0) {
          dailyDetails.push({
            date: dateKey,
            scheduled: daySchedMin,
            breaks: dayBreakMin,
            effective: dayWorkedNet,
            basePay: dayBasePay,
            extraAuthPay: dayExtraAuthPay,
            extraUnauthPay: dayExtraUnauthPay,
            isAuthorized
          });
        }

        totalScheduledMinutes += daySchedMin;
        totalBasePayment += dayBasePay;
        totalWorkedMinutesNet += dayWorkedNet;
        totalBreakMinutes += dayBreakMin;
        totalAuthorizedExtraPayment += dayExtraAuthPay;
        totalUnauthorizedExtraPayment += dayExtraUnauthPay;

        current = addDays(current, 1);
      }

      return {
        userId: user.id,
        name: user.name || user.email,
        scheduledMinutes: totalScheduledMinutes,
        workedMinutesNet: totalWorkedMinutesNet,
        breakMinutes: totalBreakMinutes,
        basePayment: totalBasePayment,
        authorizedExtraPayment: totalAuthorizedExtraPayment,
        unauthorizedExtraPayment: totalUnauthorizedExtraPayment,
        totalPayment: totalBasePayment + totalAuthorizedExtraPayment,
        shiftCount: userShifts.length,
        dailyDetails
      };
    }).filter(r => r.scheduledMinutes > 0 || r.workedMinutesNet > 0);
  }, [users, shifts, logs, startDate, endDate]);

  const downloadExcel = () => {
    const dataToExport = [];
    reportData.forEach(r => {
      dataToExport.push({
        Trabajador: r.name,
        Fecha: 'RESUMEN PERIODO',
        'Horas Prog.': (r.scheduledMinutes / 60).toFixed(2).replace('.', ','),
        'Colación (hrs)': (r.breakMinutes / 60).toFixed(2).replace('.', ','),
        'Horas Reales (Neto)': (r.workedMinutesNet / 60).toFixed(2).replace('.', ','),
        'Pago Base': r.basePayment,
        'Extras Autorizadas': r.authorizedExtraPayment,
        'Extras Por Autorizar': r.unauthorizedExtraPayment,
        'TOTAL A PAGAR': r.totalPayment
      });
      r.dailyDetails.forEach(d => {
        dataToExport.push({
          Trabajador: '',
          Fecha: d.date,
          'Horas Prog.': (d.scheduled / 60).toFixed(2).replace('.', ','),
          'Colación (hrs)': (d.breaks / 60).toFixed(2).replace('.', ','),
          'Horas Reales (Neto)': (d.effective / 60).toFixed(2).replace('.', ','),
          'Pago Base': d.basePay,
          'Extras Autorizadas': d.extraAuthPay,
          'Extras Por Autorizar': d.extraUnauthPay,
          'TOTAL A PAGAR': d.basePay + d.extraAuthPay
        });
      });
      dataToExport.push({});
    });

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Liquidacion_Detallada");
    XLSX.writeFile(wb, `Liquidacion_Detalle_${startDate}_${endDate}.xlsx`);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm">
        <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-3 bg-slate-50 rounded-xl hover:bg-slate-100 text-slate-400 transition-colors border border-slate-100">
                <ArrowLeft size={20} />
            </button>
            <div>
                <h2 className="text-2xl font-black text-slate-800 flex items-center gap-2 m-0">
                    <BarChart3 className="text-slate-400" /> Liquidación Detallada {loading && <span className="text-xs text-blue-500 animate-pulse ml-2">Calculando...</span>}
                </h2>
                <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mt-1">Haz clic en un trabajador para ver el desglose diario</p>
            </div>
        </div>

        <div className="flex items-center gap-4 bg-slate-50 p-2 rounded-2xl border border-slate-200">
            <div className="flex items-center gap-2 px-3 border-r border-slate-200">
                <Calendar size={14} className="text-slate-400"/>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-transparent text-slate-700 text-xs font-bold outline-none w-28" />
            </div>
            <div className="flex items-center gap-2 px-3">
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="bg-transparent text-slate-700 text-xs font-bold outline-none w-28" />
            </div>
            <button onClick={fetchData} className="p-2 bg-slate-800 rounded-xl text-white hover:bg-slate-900 transition-all">
                <Filter size={14} />
            </button>
        </div>

        <button onClick={downloadExcel} className="flex items-center gap-2 px-6 py-3 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-emerald-100 transition-all">
            <Download size={18} /> Exportar Excel Detallado
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-[2.5rem] overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] bg-slate-50">
                <th className="px-8 py-6">Trabajador</th>
                <th className="px-6 py-6 text-center">Horas Efectivas</th>
                <th className="px-6 py-6 text-right">Base</th>
                <th className="px-6 py-6 text-right text-emerald-600">Extras OK</th>
                <th className="px-6 py-6 text-right text-slate-300">Pendientes</th>
                <th className="px-10 py-6 text-right">Total a Pagar</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {reportData.length === 0 ? (
                 <tr><td colSpan="6" className="px-10 py-20 text-center text-slate-400 font-bold uppercase tracking-widest italic">No hay datos en este periodo</td></tr>
              ) : (
                reportData.map((row) => (
                    <React.Fragment key={row.userId}>
                        <tr 
                            className={`cursor-pointer transition-colors ${expandedUserId === row.userId ? 'bg-blue-50/30' : 'hover:bg-slate-50/50'}`}
                            onClick={() => setExpandedUserId(expandedUserId === row.userId ? null : row.userId)}
                        >
                            <td className="px-8 py-6 font-bold text-slate-700 flex items-center gap-3">
                                {expandedUserId === row.userId ? <ChevronUp size={16} className="text-blue-500"/> : <ChevronDown size={16} className="text-slate-300"/>}
                                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 font-bold text-xs uppercase">{row.name.substring(0, 2)}</div>
                                {row.name}
                            </td>
                            <td className="px-6 py-6 text-center">
                                <div className="flex flex-col items-center">
                                    <span className="text-slate-700 font-black text-sm">{formatDuration(row.workedMinutesNet)}</span>
                                    <span className="text-[10px] text-slate-400 font-black uppercase tracking-tighter">de {formatDuration(row.scheduledMinutes)}</span>
                                </div>
                            </td>
                            <td className="px-6 py-6 text-right font-bold text-slate-400 text-sm">${row.basePayment.toLocaleString()}</td>
                            <td className="px-6 py-6 text-right font-black text-emerald-600 text-sm">+${row.authorizedExtraPayment.toLocaleString()}</td>
                            <td className="px-6 py-6 text-right font-bold text-slate-300 text-sm">${row.unauthorizedExtraPayment.toLocaleString()}</td>
                            <td className="px-10 py-6 text-right">
                                <div className="flex items-center justify-end gap-2 text-slate-800">
                                    <Banknote size={16} className="text-slate-300" />
                                    <span className="text-2xl font-black tracking-tighter">${row.totalPayment.toLocaleString()}</span>
                                </div>
                            </td>
                        </tr>
                        {expandedUserId === row.userId && (
                            <tr className="bg-slate-50/50">
                                <td colSpan="6" className="px-10 py-4">
                                    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-inner animate-in slide-in-from-top-2 duration-300">
                                        <table className="w-full text-left">
                                            <thead className="bg-slate-50 text-[9px] font-black uppercase text-slate-400">
                                                <tr>
                                                    <th className="px-6 py-3">Día / Fecha</th>
                                                    <th className="px-6 py-3">H. Prog</th>
                                                    <th className="px-6 py-3">Colación</th>
                                                    <th className="px-6 py-3">H. Real (Efectivas)</th>
                                                    <th className="px-6 py-3 text-right">Pago Base</th>
                                                    <th className="px-6 py-3 text-right">Extras</th>
                                                    <th className="px-6 py-3 text-right">Estado</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {row.dailyDetails.map((day, idx) => {
                                                    const isTodayRow = isSameDay(parseISO(day.date), new Date());
                                                    return (
                                                        <tr key={idx} className={`text-xs font-bold transition-colors ${isTodayRow ? 'bg-blue-50/50 text-blue-700' : 'text-slate-600'}`}>
                                                            <td className="px-6 py-3 capitalize">
                                                                {format(parseISO(day.date), "EEEE dd/MM", { locale: es })}
                                                                {isTodayRow && <span className="ml-2 text-[8px] bg-blue-600 text-white px-1.5 py-0.5 rounded-full uppercase">Hoy</span>}
                                                            </td>
                                                            <td className="px-6 py-3 flex items-center gap-1"><Clock size={10} className={isTodayRow ? 'text-blue-400' : 'text-slate-300'}/> {formatDuration(day.scheduled)}</td>
                                                            <td className="px-6 py-3 text-slate-400">{formatDuration(day.breaks)}</td>
                                                            <td className={`px-6 py-3 ${isTodayRow ? 'text-blue-900' : 'text-slate-800'}`}>{formatDuration(day.effective)}</td>
                                                            <td className="px-6 py-3 text-right text-slate-400">${day.basePay.toLocaleString()}</td>
                                                            <td className="px-6 py-3 text-right">
                                                                {day.extraAuthPay > 0 && <span className="text-emerald-600">+${day.extraAuthPay.toLocaleString()}</span>}
                                                                {day.extraUnauthPay > 0 && <span className="text-slate-300">+${day.extraUnauthPay.toLocaleString()}</span>}
                                                                {day.extraAuthPay === 0 && day.extraUnauthPay === 0 && '-'}
                                                            </td>
                                                            <td className="px-6 py-3 text-right">
                                                                {day.isAuthorized ? <span className="text-[9px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full uppercase">OK</span> : day.extraUnauthPay > 0 ? <span className="text-[9px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full uppercase flex items-center gap-1 justify-end"><Zap size={8}/> Pendiente</span> : '-'}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
